import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Clock, Loader2, MapPin, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { FadeUp, MaskedLines, SectionLabel } from "../components/Reveal";
import StatusBadge from "../components/StatusBadge";
import { PHONE_DISPLAY, PHONE_FIXE_DISPLAY, PHONE_FIXE_TEL, PHONE_TEL, SERVICES, WHATSAPP_URL } from "../data/site";
import WhatsAppIcon from "../components/WhatsAppIcon";

const inputCls =
    "w-full border border-midnight/15 bg-white px-4 py-3.5 text-sm text-midnight outline-none transition-colors placeholder:text-midnight/35 focus:border-emergency";

export default function Contact() {
    const [form, setForm] = useState({ nom: "", telephone: "", email: "", service: SERVICES[0].title, message: "" });
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await fetch(`${process.env.PUBLIC_URL}/devis.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw new Error("http");
            setSent(true);
            toast.success("Demande envoyée — nous vous rappelons très vite.");
        } catch {
            toast.error("Envoi impossible. Appelez-nous directement au " + PHONE_DISPLAY);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <SEO
                title="Devis Gratuit & Contact Serrurier Paris | Réponse Immédiate 24/7"
                description="Devis serrurier Paris gratuit et sans engagement : réponse immédiate, prix transparent garanti. Urgence ? Appelez le 06 25 25 86 19 — intervention en 30 min, 24h/24 sur Paris & Île-de-France."
                path="/devis-contact"
            />

            <section className="grain relative overflow-hidden bg-midnight py-10 text-white md:py-16">
                <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-emergency/20 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <nav className="mb-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50" data-testid="breadcrumb">
                        <Link to="/" className="transition-colors hover:text-gold">Accueil</Link>
                        <span>/</span>
                        <span className="text-gold">Devis & Contact</span>
                    </nav>
                    <SectionLabel dark>Devis gratuit en ligne</SectionLabel>
                    <StatusBadge className="mt-6" />
                    <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
                        <MaskedLines delay={0.15} lines={["Votre devis gratuit,", "sans engagement."]} />
                    </h1>
                    <FadeUp delay={0.55}>
                        <p className="mt-6 max-w-xl leading-relaxed text-white/70">
                            Décrivez votre besoin : nous nous engageons à vous fournir un devis de serrurier détaillé,
                            suivi de nos conseils. Urgence ? L'appel reste le plus rapide.
                        </p>
                    </FadeUp>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <div className="grid gap-10 lg:grid-cols-[1fr_1.3fr] lg:gap-16">
                    {/* INFOS */}
                    <div className="space-y-5">
                        <FadeUp>
                            <a
                                href={PHONE_TEL}
                                data-testid="contact-call-card"
                                className="group flex items-center justify-between bg-emergency p-7 text-white transition-colors hover:bg-emergency-dark"
                            >
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] opacity-85">Appel direct — le plus rapide</p>
                                    <p className="mt-2 font-display text-3xl font-semibold tracking-tight">{PHONE_DISPLAY}</p>
                                    <p className="mt-1 text-sm opacity-85">Intervention en 30 minutes, 24h/24</p>
                                </div>
                                <Phone className="h-10 w-10 transition-transform duration-300 group-hover:-rotate-12" />
                            </a>
                        </FadeUp>
                        <FadeUp delay={0.04}>
                            <a
                                href={PHONE_FIXE_TEL}
                                data-testid="contact-fixe-card"
                                className="group flex items-center justify-between bg-midnight p-7 text-white transition-colors hover:bg-navy"
                            >
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold">Ligne devis gratuite</p>
                                    <p className="mt-2 font-display text-3xl font-extrabold tracking-tight">{PHONE_FIXE_DISPLAY}</p>
                                    <p className="mt-1 text-sm text-white/60">Estimation et conseil, sans engagement</p>
                                </div>
                                <Phone className="h-10 w-10 transition-transform duration-300 group-hover:-rotate-12" />
                            </a>
                        </FadeUp>
                        <FadeUp delay={0.08}>
                            <a
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-whatsapp-card"
                                className="flex items-center justify-between border border-[#25D366]/30 bg-[#25D366]/5 p-7 transition-colors hover:bg-[#25D366]/10"
                            >
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#128C4A]">WhatsApp</p>
                                    <p className="mt-2 font-display text-xl font-semibold tracking-tight">Écrivez-nous, photo de la serrure bienvenue</p>
                                </div>
                                <WhatsAppIcon className="h-9 w-9 text-[#25D366]" />
                            </a>
                        </FadeUp>
                        <FadeUp delay={0.16}>
                            <div className="space-y-4 border border-midnight/10 bg-white p-7" data-testid="contact-infos">
                                <p className="flex items-center gap-3 text-sm font-semibold">
                                    <Clock className="h-5 w-5 text-emergency" /> 24h/24 — 7j/7, week-ends et jours fériés
                                </p>
                                <p className="flex items-center gap-3 text-sm font-semibold">
                                    <MapPin className="h-5 w-5 text-emergency" /> Paris (20 arrondissements) & Île-de-France
                                </p>
                                <p className="border-t border-midnight/10 pt-4 text-sm leading-relaxed text-midnight/60">
                                    Devis gratuit et sans engagement. Un devis écrit vous est remis avant toute intervention — c'est notre pratique quotidienne.
                                </p>
                            </div>
                        </FadeUp>
                    </div>

                    {/* FORM */}
                    <FadeUp delay={0.1}>
                        <div className="border border-midnight/10 bg-white p-8 md:p-10">
                            {sent ? (
                                <div className="flex h-full min-h-[420px] flex-col items-center justify-center text-center" data-testid="devis-success">
                                    <CheckCircle2 className="h-14 w-14 text-emergency" strokeWidth={1.5} />
                                    <h2 className="mt-6 font-display text-3xl font-semibold tracking-tight">Demande bien reçue</h2>
                                    <p className="mt-4 max-w-sm text-sm leading-relaxed text-midnight/60">
                                        Un artisan serrurier vous rappelle dans les plus brefs délais avec votre devis
                                        détaillé. Pour une urgence immédiate, appelez le {PHONE_DISPLAY}.
                                    </p>
                                    <a
                                        href={PHONE_TEL}
                                        className="mt-8 inline-flex items-center gap-2 bg-emergency px-7 py-4 font-bold text-white transition-colors hover:bg-emergency-dark"
                                    >
                                        <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
                                    </a>
                                </div>
                            ) : (
                                <form onSubmit={submit} data-testid="devis-form" className="space-y-5">
                                    <h2 className="font-display text-2xl font-semibold tracking-tight">Demande de devis</h2>
                                    <div className="grid gap-5 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor="nom" className="mb-2 block text-xs font-bold uppercase tracking-wider text-midnight/60">Nom *</label>
                                            <input id="nom" data-testid="devis-field-nom" required value={form.nom} onChange={update("nom")} placeholder="Votre nom" className={inputCls} />
                                        </div>
                                        <div>
                                            <label htmlFor="telephone" className="mb-2 block text-xs font-bold uppercase tracking-wider text-midnight/60">Téléphone *</label>
                                            <input id="telephone" data-testid="devis-field-telephone" required type="tel" value={form.telephone} onChange={update("telephone")} placeholder="06 00 00 00 00" className={inputCls} />
                                        </div>
                                    </div>
                                    <div className="grid gap-5 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor="email" className="mb-2 block text-xs font-bold uppercase tracking-wider text-midnight/60">Email</label>
                                            <input id="email" data-testid="devis-field-email" type="email" value={form.email} onChange={update("email")} placeholder="vous@exemple.fr" className={inputCls} />
                                        </div>
                                        <div>
                                            <label htmlFor="service" className="mb-2 block text-xs font-bold uppercase tracking-wider text-midnight/60">Service souhaité *</label>
                                            <select id="service" data-testid="devis-field-service" value={form.service} onChange={update("service")} className={inputCls}>
                                                {SERVICES.map((s) => (
                                                    <option key={s.title} value={s.title}>{s.title}</option>
                                                ))}
                                                <option value="Intervention après cambriolage">Intervention après cambriolage</option>
                                                <option value="Autre demande">Autre demande</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="message" className="mb-2 block text-xs font-bold uppercase tracking-wider text-midnight/60">Votre besoin *</label>
                                        <textarea
                                            id="message"
                                            data-testid="devis-field-message"
                                            required
                                            rows={5}
                                            value={form.message}
                                            onChange={update("message")}
                                            placeholder="Décrivez votre situation : type de porte, étage, arrondissement, urgence…"
                                            className={`${inputCls} resize-none`}
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        data-testid="devis-submit-button"
                                        disabled={loading}
                                        className="flex w-full items-center justify-center gap-3 bg-midnight py-4 font-bold text-white transition-colors duration-300 hover:bg-emergency disabled:opacity-60"
                                    >
                                        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                                        {loading ? "Envoi en cours…" : "Envoyer ma demande de devis"}
                                    </button>
                                    <p className="text-xs leading-relaxed text-midnight/45">
                                        Vos données servent uniquement à traiter votre demande. Elles ne sont ni
                                        vendues, ni partagées. Voir nos{" "}
                                        <Link to="/mentions-legales" className="underline">mentions légales</Link>.
                                    </p>
                                </form>
                            )}
                        </div>
                    </FadeUp>
                </div>
            </section>
        </div>
    );
}
